import client from "@/api/openapi-ts";
import { defineStore, acceptHMRUpdate } from "pinia";
import { ref } from "vue";
import { Notification } from "@/types/api/Notification";
import { usePlugins } from "./helpers/usePlugins";
import { PaginationArgs, PaginatedData } from "@/types/api/common";

export const useNotificationStore = defineStore("notification", () => {
  const loading = ref(false);
  const saving = ref(false);
  const notifications = ref<Notification[]>([]);
  const adminNotifications = ref<PaginatedData<Notification>>({});
  const { $ws } = usePlugins();

  return {
    loading,
    saving,
    notifications,
    adminNotifications,

    handleReceivedNotification: (payload: Notification) => {
      const notification = notifications.value.find((n) => n.id === payload.id);

      if (notification) {
        Object.assign(notification, payload);
      } else {
        notifications.value.unshift(payload);
      }
    },
    updateNotification: (payload: Notification) => {
      if ($ws) {
        $ws.sendMessage({
          action: "notification_update_read",
          payload,
        });
      }
    },
    removeNotification: (payload: Notification) => {
      const idx = notifications.value.findIndex((n) => n.id === payload.id);

      if (idx >= 0) {
        notifications.value.splice(idx, 1);
        if ($ws) {
          $ws.sendMessage({
            action: "notification_delete",
            payload,
          });
        }
      }
    },
    async index() {
      loading.value = true;
      try {
        const { data } = await client.GET("/api/v1/notifications");
        notifications.value = (data as unknown as Notification[]) ?? [];
      } finally {
        loading.value = false;
      }
    },
    async create(payload: Notification) {
      saving.value = true;
      try {
        const { data, error } = await client.POST(
          "/api/v1/notifications/admin",
          {
            body: payload as never,
          },
        );
        if (error) {
          throw error;
        }
        return data;
      } finally {
        saving.value = false;
      }
    },
    async removeAll() {
      const { error } = await client.DELETE("/api/v1/notifications");
      if (!error) {
        notifications.value = [];
      }
    },
    async adminIndex(params: PaginationArgs) {
      loading.value = true;
      try {
        const { data, error, response } = await client.GET(
          "/api/v1/notifications/admin",
          {
            params: { query: params as never },
          },
        );
        if (error) {
          throw error;
        }
        adminNotifications.value = {
          pagination: {
            page: Number(response.headers.get("current-page")),
            page_items: Number(response.headers.get("page-items")),
            total_pages: Number(response.headers.get("total-pages")),
            total_items: Number(response.headers.get("total-count")),
          },
          data: (data as unknown as Notification[]) ?? [],
        };
      } finally {
        loading.value = false;
      }
    },
    async adminUpdateHeaderNotification(message: string) {
      saving.value = true;
      try {
        const { error } = await client.PUT(
          "/api/v1/notifications/admin/header",
          {
            body: { message } as never,
          },
        );
        if (error) {
          throw error;
        }
      } finally {
        saving.value = false;
      }
    },
    adminDeleteHeaderNotification() {
      return client.DELETE("/api/v1/notifications/admin/header");
    },
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useNotificationStore, import.meta.hot),
  );
}
